import React from 'react';
import { Tooltip, TooltipProps } from '@cp/ds/src/components/tooltip';
import { Box } from '@cp/ds/src/components/box';
import { MenuItemProps } from '../model/item.interface';
import { BottomMenuContext } from '../model/context';
import { BottomMenuItem } from './menuItem';
import { BOTTOM_MENU_VISIBILITY_ANIMATION_DURATION_IN_MS } from './const';

export interface MenuItemWithTimedHintsProps {
  config: MenuItemProps;
  menuContainerRef: React.RefObject<HTMLDivElement>;
  hints: React.ReactNode;
  delayToShowInMs: number;
  tooltipProps?: Partial<TooltipProps>;
}

export const MenuItemWithTimedHints: React.FC<MenuItemWithTimedHintsProps> = ({
  config,
  menuContainerRef,
  delayToShowInMs,
  hints,
  tooltipProps,
}) => {
  const menuContext = React.useContext(BottomMenuContext);
  const [isMessageCanBeVisible, setMessageCanBeVisible] = React.useState(menuContext.isVisible);
  const [isMessageVisible, setMessageVisible] = React.useState(false);
  const isTriggered = React.useRef(false);

  React.useEffect(() => {
    if (!menuContext.isVisible) {
      setMessageCanBeVisible(false);
    } else {
      setTimeout(() => {
        setMessageCanBeVisible(true);
      }, BOTTOM_MENU_VISIBILITY_ANIMATION_DURATION_IN_MS);
    }
  }, [menuContext.isVisible]);

  React.useEffect(() => {
    if (isTriggered.current) {
      return;
    }

    const menuContainer = menuContainerRef.current;
    const handleMessageDisappear = () => setMessageVisible(false);

    setTimeout(() => {
      setMessageVisible(true);
      isTriggered.current = true;
      menuContainer?.addEventListener('click', handleMessageDisappear);
    }, delayToShowInMs);

    return () => {
      menuContainer?.removeEventListener('click', handleMessageDisappear);
    };
  }, [delayToShowInMs, menuContainerRef]);

  return (
    <Tooltip
      arrow
      colorSchema="dark"
      disableFocusListener
      disableHoverListener
      disableTouchListener
      onClose={() => setMessageVisible(false)}
      open={isMessageCanBeVisible && isMessageVisible}
      slotProps={{
        popper: {
          disablePortal: true,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        },
      }}
      title={<Box sx={{ px: 1.75, py: 1 }}>{hints}</Box>}
      {...tooltipProps}
    >
      <div>
        <BottomMenuItem {...config} />
      </div>
    </Tooltip>
  );
};
